import { Epic, ofType } from 'redux-observable';
import { of, concat, EMPTY } from 'rxjs';
import { mergeMap, delay, tap, map, catchError } from 'rxjs/operators';
import { ENV, GENERAL } from '../../../constants';
import { coreState } from '../core';
import { IAction, IRootState, IEpicDependencies } from '../rootState';
import { actions, ActionType } from './actions';
import { generalState } from '.';
import { GeneralModel } from 'modules/models';

export const addToast: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.ADD_TOAST_START),
    mergeMap(({ payload }) => {
      const _id = Date.now().toString();
      return concat(of(actions.addToastSuccess(_id, payload.message, payload.type)), of(actions.removeToast(_id)).pipe(delay(ENV.TOAST_DELAY)));
    })
  );

export const destroyPrinter: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.DESTROY_PRINTER),
    tap(() => deps.printerService.destroy()),
    mergeMap(() => EMPTY)
  );

export const fetchCountryListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_COUNTRY_LIST_START),
    mergeMap(() =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_COUNTRY_LIST, true)),
        deps.apiService.getCountryList().pipe(map(res => actions.fetchCountryListSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_COUNTRY_LIST, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_COUNTRY_LIST, false, true, error))))
    )
  );

export const fetchTimeZoneListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_TIME_ZONE_LIST_START),
    mergeMap(() =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_TIME_ZONE_LIST, true)),
        deps.apiService.getTimeZones().pipe(map(res => actions.fetchTimeZoneListSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_TIME_ZONE_LIST, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_TIME_ZONE_LIST, false, true, error))))
    )
  );

export const fetchSearchStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SEARCH_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH, true)),
        deps.apiService.quickSearch(payload.search).pipe(map(res => actions.fetchSearchSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH, false, true, error))))
    )
  );

export const fetchSearchMoreStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SEARCH_MORE_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_MORE, true)),
        deps.apiService.quickSearch(payload.search).pipe(map(res => actions.fetchSearchMoreSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_MORE, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_MORE, false, true, error))))
    )
  );

export const fetchSearchNewStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SEARCH_NEW_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH, true)),
        deps.apiService.quickSearchConsolidated(payload.search).pipe(map(res => actions.fetchSearchNewSuccess(res, payload?.search.searchType))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH, false, true, error))))
    )
  );

export const fetchSearchNewMoreStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_SEARCH_NEW_MORE_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_MORE, true)),
        deps.apiService.quickSearchConsolidated(payload.search).pipe(map(res => actions.fetchSearchNewMoreSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_MORE, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_SEARCH_MORE, false, true, error))))
    )
  );

export const fetchFeatureListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_FEATURE_FLAG_START),
    mergeMap(() =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_FEATURE_FLAG, true)),
        deps.apiService.getFeatureList().pipe(map(res => actions.fetchFeatureListSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_FEATURE_FLAG, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_FEATURE_FLAG, false, true, error))))
    )
  );

export const fetchConsentFormFieldsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CONSENT_FORM_FIELDS_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_CONSENT_FORM_FIELDS, true)),
        deps.apiService
          .getConsentFormFields(payload.consentFormFieldType, payload.companyIds, undefined, undefined, payload.includeUsageCount, payload.includeListItems)
          .pipe(map(res => actions.fetchConsentFormFieldsSuccess(res, payload.consentFormFieldType))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_CONSENT_FORM_FIELDS, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_CONSENT_FORM_FIELDS, false, true, error))))
    )
  );

export const fetchRegularExpressionsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_REGULAR_EXPRESSIONS_START),
    mergeMap(() =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_REGULAR_EXPRESSIONS, true)),
        deps.apiService.getRegularExpressions().pipe(map(res => actions.fetchRegularExpressionsSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_REGULAR_EXPRESSIONS, false))
      ).pipe(catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_REGULAR_EXPRESSIONS, false, true, error))))
    )
  );

export const saveConsentFormFieldsBulkStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SAVE_CONSENT_FORM_FIELDS_BULK_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.SAVE_CONSENT_FORM_FIELDS_BULK, true)),
        deps.apiService
          .saveConsentFormFieldsBulk(payload.payload)
          .pipe(
            mergeMap(() =>
              of(
                generalState.actions.addToastStart('Custom consent fields added successfully!', GeneralModel.ToastType.SUCCESS),
                actions.fetchConsentFormFieldsStart(payload.consentFormFieldType, payload.companyIds, undefined, undefined)
              )
            )
          ),
        of(actions.setLoading(GENERAL.LOADING_KEY.SAVE_CONSENT_FORM_FIELDS_BULK, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_CONSENT_FORM_FIELDS_BULK, false, true, error),
            generalState.actions.addToastStart(
              Object.keys(error.response?.errors || {})[0] ? Object.values(error.response?.errors || {})[0] : error.title,
              GeneralModel.ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const updateConsentFormFieldByIdStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_CONSENT_FORM_FIELD_BY_ID_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CONSENT_FORM_FIELD_BY_ID, true)),
        deps.apiService
          .updateConsentFormFieldById(payload.id, payload.payload)
          .pipe(
            mergeMap(() =>
              of(
                generalState.actions.addToastStart('Custom consent fields updated successfully!', GeneralModel.ToastType.SUCCESS),
                actions.fetchConsentFormFieldsStart(payload.consentFormFieldType, payload.companyIds, undefined, undefined)
              )
            )
          ),
        of(actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CONSENT_FORM_FIELD_BY_ID, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_CONSENT_FORM_FIELD_BY_ID, false, true, error),
            generalState.actions.addToastStart(
              Object.keys(error.response?.errors || {})[0] ? Object.values(error.response?.errors || {})[0] : error.title,
              GeneralModel.ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const deleteConsentFormFieldStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.DELETE_CONSENT_FORM_FIELD_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CONSENT_FORM_FIELD, true)),
        deps.apiService
          .deleteConsentFormField(payload.id)
          .pipe(mergeMap(() => [actions.fetchConsentFormFieldsStart(payload.consentFormFieldType, payload.companyIds, undefined, undefined)])),
        of(generalState.actions.addToastStart(`Consent form field deleted successfully!`, GeneralModel.ToastType.SUCCESS)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CONSENT_FORM_FIELD, false))
      ).pipe(
        catchError(error =>
          of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_CONSENT_FORM_FIELD, false, true, error),
            generalState.actions.addToastStart(error.title, GeneralModel.ToastType.ERROR)
          )
        )
      )
    )
  );

export const fetchBillingEngineSettingsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_BILLING_ENGINE_SETTINGS_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_BILLING_ENGINE_SETTINGS, true)),
        deps.apiService.getBillingEngineSettings(payload.id).pipe(map(res => actions.fetchBillingEngineSettingsSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.FETCH_BILLING_ENGINE_SETTINGS, false))
      ).pipe(
        catchError(error => of(coreState.actions.epicError(error), actions.setLoading(GENERAL.LOADING_KEY.FETCH_BILLING_ENGINE_SETTINGS, false, true, error)))
      )
    )
  );

export const updateBillingEngineSettingsStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_BILLING_ENGINE_SETTINGS_START),
    mergeMap(({ payload }) =>
      concat(
        of(actions.setLoading(GENERAL.LOADING_KEY.UPDATE_BILLING_ENGINE_SETTINGS, true)),
        deps.apiService.updateBillingEngineSettings(payload.id, payload.payload).pipe(map(res => actions.fetchBillingEngineSettingsSuccess(res))),
        of(actions.setLoading(GENERAL.LOADING_KEY.UPDATE_BILLING_ENGINE_SETTINGS, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_BILLING_ENGINE_SETTINGS, false, true, error),
            generalState.actions.addToastStart(
              Object.keys(error.response?.errors || {})[0] ? Object.values(error.response?.errors || {})[0] : error.title,
              GeneralModel.ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const epics = [
  addToast,
  destroyPrinter,
  fetchCountryListStart,
  fetchTimeZoneListStart,
  fetchSearchStart,
  fetchSearchMoreStart,
  fetchSearchNewStart,
  fetchSearchNewMoreStart,
  fetchFeatureListStart,
  fetchConsentFormFieldsStart,
  saveConsentFormFieldsBulkStart,
  updateConsentFormFieldByIdStart,
  deleteConsentFormFieldStart,
  fetchRegularExpressionsStart,
  updateBillingEngineSettingsStart,
  fetchBillingEngineSettingsStart,
];
